import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Approvers } from '../../shared/approvers/approvers';
import { Approver } from './type';
import { StatusIconProtoProps } from '@nike/eds';
import { useI18nContext } from 'context/language/use-i18n-context';
import ts from './i18n';
import { usePassPolicies } from 'context/PassPolicy';
import { useUserContext } from '../../../context/user';
import { CompForm } from './comp-form/comp-form';
import { StoreOption } from './type';
import { GetPassLocations } from '../../../services/pass-location.service';
import { PassLocation } from '../../../models/IPassLocation';
import { CompPassRequest } from '../../../models/ICompPass';
import { VisitorPass } from '../../../models/IVisitorPass';
import dayjs from 'dayjs';

type Props = {
  onDismiss: () => void;
  setSnackBar: Dispatch<
    SetStateAction<{
      showSnack: boolean;
      message: string;
      snackStatus: StatusIconProtoProps['status'];
    }>
  >;
  editCompPassModal?: VisitorPass | null;
};

export const CompPass: FC<Props> = ({ onDismiss, setSnackBar, editCompPassModal }) => {
  const { i18nString } = useI18nContext();
  const passPoliciesContext = usePassPolicies();
  const compPolicy = passPoliciesContext.passPolicies
    .filter(x => x.passTypeId === 30)
    .sort(x => x.passPolicyId)[0];
  const { passplayUser } = useUserContext();

  const compPassDefault: CompPassRequest = {
    approver: '',
    passId: 0,
    sponsorId: 0,
    passTypeId: 30,
    firstName: '',
    lastName: '',
    shoppingDateStart: '',
    shoppingDateEnd: '',
    companyName: '',
    compAmount: '',
    compNumber: '',
    passLocationId: 0,
    passPolicyId: 0,
    shoppingDays: 0,
  };

  const [compModal, setCompModal] = useState<CompPassRequest>(compPassDefault);
  const [approver, setApprover] = useState<Approver>();
  const [showApprover] = useState<boolean>(() => {
    const isSelfApprover = passplayUser?.selfApprover ?? false;
    if (isSelfApprover) {
      setApprover({
        id: passplayUser?.selfApprovalId ? passplayUser?.selfApprovalId : '',
        name: passplayUser?.userName ? passplayUser?.userName : '',
      });
    }
    return !isSelfApprover;
  });

  const [stores, setStores] = useState<StoreOption[]>([]);

  const sortPassLocations = (locations: PassLocation[]): StoreOption[] => {
    return (
      locations
        .sort((a, b) => a.store.localeCompare(b.store))
        .map(store => ({
          value: store.passLocationId,
          label: store.store,
          shoppingDays: store.shoppingDays,
        })) || []
    );
  };

  useEffect(() => {
    if (compPolicy !== null) {
      GetPassLocations(compPolicy.passLocationGroupId).then(response => {
        const selectStore: StoreOption = {
          value: 0,
          label: i18nString(ts.DDL_STORE_SELECT),
          shoppingDays: 0,
        };
        const storeOptions = sortPassLocations(response.data);
        setStores([selectStore, ...storeOptions]);

        if (editCompPassModal === null) {
          setCompModal(prev => ({
            ...prev,
            passPolicyId: compPolicy.passPolicyId,
            approver: approver?.id ?? '',
            sponsorId: passplayUser?.sponsorId ?? 0,
            shoppingDays: compPolicy.shoppingDays,
          }));
        } else {
          const isCheck = validateEditCompPassModal(editCompPassModal);

          if (!isCheck) {
            setSnackBar({
              showSnack: true,
              message: i18nString(ts.ERROR_GENERAL_1),
              snackStatus: 'error',
            });
            onDismiss();
          }

          setCompModal(prev => ({
            ...prev,
            approver: approver?.id ?? '',
            passId: editCompPassModal?.passId ?? 0,
            sponsorId: passplayUser?.sponsorId ?? 0,
            passTypeId: editCompPassModal?.passTypeId ?? 30,
            firstName: editCompPassModal?.firstName ?? '',
            lastName: editCompPassModal?.lastName ?? '',
            shoppingDateStart:
              dayjs(editCompPassModal?.shoppingDateStart).format('YYYY-MM-DD') ?? '',
            companyName: editCompPassModal?.companyName ?? '',
            compAmount: editCompPassModal?.compAmount.toString() ?? '',
            compNumber: editCompPassModal?.compNumber.toString() ?? '',
            passLocationId: editCompPassModal?.passLocationId ?? 0,
            passPolicyId: editCompPassModal?.passPolicyId ?? 0,
            shoppingDays: compPolicy.shoppingDays,
          }));
        }
      });
    }
  }, [compPolicy, approver]);

  const validateEditCompPassModal = (modal: VisitorPass | null | undefined): boolean => {
    if (!modal) return false;
    const { passTypeId, passId, passPolicyId } = modal;
    return passTypeId === 30 && !!passId && !!passPolicyId;
  };

  const handleApproverChange = (option: any) => {
    setApprover({ id: option.value, name: option.label });
  };

  return (
    <div data-testid="comp-pass" className="pass_modal_width">
      {showApprover && <Approvers onApproverChange={handleApproverChange} />}
      <section
        className={`eds-flex eds-flex--direction-column eds-flex--justify-content-center ${showApprover ? 'eds-spacing--mt-16' : ''}`}
      >
        <CompForm
          onDismiss={onDismiss}
          compPassToEdit={compModal}
          stores={stores}
          approver={approver}
          setSnackBar={setSnackBar}
        />
      </section>
    </div>
  );
};
